import type React from "react";
import {Tooltip} from "@mui/material";
import {Tag} from "~/components/tag/Tag.tsx";
import type {ContractLinkage, ContractParty} from "../contractExplorerTypes.ts";

type Props = {
    party: ContractParty;
    linkage: ContractLinkage;
    /** How many parties linkage used as buyers; their entities are matched together. */
    buyers: number;
};

const USED_LABEL: Record<string, string> = {supplier: 'Supplier', buyer: 'Buying entity'};

/** What one party resolved to in the spend data, as linkage used it. */
export const PartyMatch: React.FC<Props> = ({party, linkage, buyers}) => {
    if (!linkage.state) return <span className="ci-muted">No linkage result</span>;
    if (!party.used_as) {
        return party.role === 'other'
            ? <span className="ci-muted" title="Linkage looks up suppliers, buyers and parties whose role is unclear.">
                Not looked up</span>
            : <span className="ci-muted" title="Linkage matched another party in this role, or found no match for this one.">
                Not matched</span>;
    }
    const resolved = party.used_as === 'supplier' ? [linkage.supplier_label] : linkage.entities;
    return (
        <span className="ci-party-m">
            <span className="ci-party-tags">
                <span className="ci-party-r">{USED_LABEL[party.used_as] ?? party.used_as}</span>
                {party.corrected && (
                    <Tooltip arrow title={`The documents name it as the ${party.role}, but the spend data has it as the `
                        + `${party.used_as}, so linkage swapped the roles.`}>
                        <span><Tag label="Roles swapped" color="warning"/></span>
                    </Tooltip>
                )}
            </span>
            <span className="ci-party-v">
                {party.used_as === 'buyer' && buyers > 1 && <span className="ci-csub">With the other buyers: </span>}
                {resolved.filter(Boolean).join(' · ') || '—'}
            </span>
        </span>
    );
};
